import type {
  PlannerGridPosition,
  VesselPlannerSetting,
} from "../../types/VesselPlannerSetting";

export const DEFAULT_VESSEL_PLANNER_SETTING: VesselPlannerSetting = {
  version: 1,
  excludedPositions: [],
  stackingSequence: [],
  showSequenceNumbers: true,
  groupingPreference: "fullBeamStrips",
  incompleteLayerPreference: "middleRowsFirst",
  constraints: {
    respectFortyOnTwenty: true,
    balanceOverBeam: true,
    balanceOverLength: true,
    segregateAdn: false,
    avoidShifters: true,
  },
};

export const plannerPositionKey = (x: number, y: number) => `${x}-${y}`;

export const getUsableVesselSlots = (gridCount: unknown): number[] => {
  const count = Math.max(0, Number(gridCount) || 0);
  if (count === 2) return [2, 3];
  if (count === 3) return [2, 3, 4];
  return Array.from({ length: count }, (_, index) => index + 1);
};

export const normalizeVesselPlannerSetting = (
  value: unknown
): VesselPlannerSetting => {
  const candidate = value as Partial<VesselPlannerSetting> | null | undefined;
  const normalizePositions = (positions: unknown): PlannerGridPosition[] => {
    const seen = new Set<string>();
    const result: PlannerGridPosition[] = [];
    if (!Array.isArray(positions)) return result;
    positions.forEach((position) => {
      if (!Array.isArray(position) || position.length < 2) return;
      const x = Number(position[0]);
      const y = Number(position[1]);
      if (!Number.isInteger(x) || !Number.isInteger(y) || x < 1 || y < 1) return;
      const key = plannerPositionKey(x, y);
      if (seen.has(key)) return;
      seen.add(key);
      result.push([x, y]);
    });
    return result;
  };

  const groupingPreference = ["fullBeamStrips", "centerBlocks", "smallPiles"].includes(
    String(candidate?.groupingPreference)
  )
    ? (candidate?.groupingPreference as VesselPlannerSetting["groupingPreference"])
    : DEFAULT_VESSEL_PLANNER_SETTING.groupingPreference;
  const incompleteLayerPreference = ["middleRowsFirst", "outerRowsFirst"].includes(
    String(candidate?.incompleteLayerPreference)
  )
    ? (candidate?.incompleteLayerPreference as VesselPlannerSetting["incompleteLayerPreference"])
    : DEFAULT_VESSEL_PLANNER_SETTING.incompleteLayerPreference;
  const constraints = candidate?.constraints || {};

  return {
    version: 1,
    excludedPositions: normalizePositions(candidate?.excludedPositions),
    stackingSequence: normalizePositions(candidate?.stackingSequence),
    showSequenceNumbers: candidate?.showSequenceNumbers !== false,
    groupingPreference,
    incompleteLayerPreference,
    constraints: {
      respectFortyOnTwenty: true,
      balanceOverBeam: constraints.balanceOverBeam !== false,
      balanceOverLength: constraints.balanceOverLength !== false,
      segregateAdn: constraints.segregateAdn === true,
      avoidShifters: constraints.avoidShifters !== false,
    },
  };
};

export const getExcludedPlannerPositionKeys = (vessel: any) =>
  new Set(
    normalizeVesselPlannerSetting(
      vessel?.plannerSettings ?? vessel?.plannerSetting
    ).excludedPositions.map(
      ([x, y]) => plannerPositionKey(x, y)
    )
  );
